<template>
  <section class="flex align-middle justify-center sm:py-40">
    <div class="flex flex-col">
      <a class="link--primary mb-16" :href="redirectTo">
        <div class="inline-block md:pl-0 mt-4"><BackIcon /></div>
        <p class="inline-block ml-8">Pricing</p>
      </a>
      <Card class="w-full max-w-448 p-24">
        <form novalidate @submit.prevent="submit()">
          <h1 class="text-h1 mb-16">Request an upgrade</h1>
          <fieldset>
            <legend class="pb-16 text-body text-grey-body">
              Check if your company’s benefits include unlimited access to all of Sittercity’s features
            </legend>
            <SearchComboBox
              id="companySearchBox"
              ref="companySearchBox"
              v-model="employerNameInput"
              allow-empty-results
              :results="filteredCorporateClients"
              type="text"
              label-name="Your employer’s name"
              :initial-value="initialValue"
              class="mb-16"
              :error-text="employerErrorText"
              :status="employerStatus"
              @selected="updateValue"
              @value-updated="filterResults"
            />
            <text-field
              id="workEmail"
              v-model="workEmail"
              label-name="Work email"
              :helper-text="emailFieldHelperText"
              name="workEmail"
              type="email"
              autocomplete="email"
              class="my-16"
              :status="workEmailStatus"
            />
          </fieldset>
          <Button class="w-full" variant="primary" value="Continue" size="medium" type="submit" :loading="isLoading" />
        </form>
      </Card>
      <popup id="errorPopup" v-model="showPopup" popup-header="We couldn’t send your request" @close="closePopup">
        <p class="text-body mb-16">
          There was an error submitting your request. Try again and if the error continues,
          <a
            href="https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=5652055814427"
            class="text-grey-clickable underline"
            >contact Customer Success</a
          >
        </p>
        <Button
          id="tryAgain"
          variant="primary"
          value="Try again"
          size="medium"
          class="w-full"
          :loading="isLoading"
          @click="closePopup"
        />
      </popup>
    </div>
  </section>
</template>

<script lang="ts">
import Card from '@/components/card/Card.vue';
import TextField from '@/components/textfield/TextField.vue';
import Button from '@/components/button/Button.vue';
import Popup from '@/components/popup/Popup.vue';
import BackIcon from '@/icons/BackIcon.vue';
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import helperStatus from '@/helpers/status';
import { validateEmail } from '@/helpers/emailHelper';
import { CORPORATE_CLIENTS_QUERY } from '@/graphql/bh_benefits/queries';
import { ADD_EMPLOYER, GENERATE_CORPORATE_VERIFICATION_TOKEN } from '@/graphql/bh_benefits/mutations';
import { sendSegmentEvent } from '@/lib/segment';
import { isOn } from '@/lib/growthbook';
import {
  CorporateVerificationTokenResult,
  resolveCorporateVerificationTokenResult,
} from '@/apps/bhBenefits/upgrade/helpers/corporateVerificationTokenResult';

const UPGRADE_SOURCE = 'premium_upgrade';

export default {
  name: 'RequestUpgrade',
  components: {
    Card,
    BackIcon,
    TextField,
    Button,
    Popup,
    SearchComboBox,
  },
  props: {
    redirectTo: { type: String, default: '' },
    preselectedCompany: { type: Object, default: () => ({}) },
  },
  data(): any {
    // Prefer slug-only preselect; corp client name is applied after CORPORATE_CLIENTS_QUERY
    // so we don't briefly show the BH search label and open the dropdown.
    return {
      showPopup: false,
      corporateClients: [],
      employerNameInput: '',
      selectedCompanySlug: this.preselectedCompany?.slug || '',
      employerStatus: helperStatus.Neutral,
      filteredCorporateClients: [],
      workEmail: '',
      workEmailStatus: helperStatus.Neutral,
      initialValue: '',
      isLoading: false,
    };
  },
  computed: {
    automatedCorporateUpgradesEnabled(): boolean {
      return isOn('sc-automate-corporate-upgrades-enabled');
    },
    employerErrorText(): string {
      if (this.validateEmployerEntered(this.employerNameInput)) {
        return 'Please select your employer from the list';
      }
      return "Employer's name is required";
    },
    emailFieldHelperText() {
      if (this.workEmailStatus === helperStatus.Error) {
        if (this.workEmail.length) {
          return 'Enter a valid email';
        }
        return 'Work email is required';
      }
      return 'Enter the email you use at work';
    },
  },
  watch: {
    employerNameInput() {
      if (this.employerStatus === helperStatus.Error && this.isEmployerSelectionValid()) {
        this.employerStatus = helperStatus.Neutral;
      }
    },
    workEmail(value) {
      if (this.workEmailStatus === helperStatus.Error) {
        if (validateEmail(value)) {
          this.workEmailStatus = helperStatus.Neutral;
        }
      }
    },
  },
  methods: {
    closePopup() {
      this.showPopup = false;
    },
    validateEmployerEntered(employer: string): boolean {
      return employer?.trim().length > 0;
    },
    isEmployerSelectionValid(): boolean {
      return !!this.selectedCompanySlug?.length;
    },
    validateWorkEmail(): boolean {
      const valid = validateEmail(this.workEmail);

      if (!valid) {
        this.workEmailStatus = helperStatus.Error;
      }

      return valid;
    },
    validateEmployerSelection(): boolean {
      const valid = this.isEmployerSelectionValid();

      if (!valid) {
        this.employerStatus = helperStatus.Error;
      }

      return valid;
    },
    async submit() {
      this.employerStatus = helperStatus.Neutral;

      const emailValid = this.validateWorkEmail();
      const employerValid = this.validateEmployerSelection();

      if (emailValid && employerValid) {
        await this.addEmployerMutation();
      }
    },
    filterResults(input) {
      this.employerNameInput = input;
      this.selectedCompanySlug = '';
      if (this.employerStatus === helperStatus.Error) {
        this.employerStatus = helperStatus.Neutral;
      }
      const searchRegex = new RegExp(input, 'i');
      this.filteredCorporateClients = this.corporateClients.filter((item) => searchRegex.test(item.display));
    },
    updateValue(v): void {
      this.selectedCompanySlug = v.value;
      this.employerNameInput = v.display;
      if (v.value) {
        this.employerStatus = helperStatus.Neutral;
      }
    },
    async submitLegacyEmployerMutation() {
      await this.$apollo.mutate({
        mutation: ADD_EMPLOYER,
        variables: {
          workEmail: this.workEmail,
          employerSlug: this.selectedCompanySlug,
          employerName: this.employerNameInput,
          source: UPGRADE_SOURCE,
        },
      });
    },
    async generateCorporateVerificationToken(): Promise<CorporateVerificationTokenResult> {
      try {
        await this.$apollo.mutate({
          mutation: GENERATE_CORPORATE_VERIFICATION_TOKEN,
          variables: {
            workEmail: this.workEmail,
            employerSlug: this.selectedCompanySlug,
          },
        });
        return 'verification';
      } catch (error) {
        return resolveCorporateVerificationTokenResult(error);
      }
    },
    async addEmployerMutation() {
      this.isLoading = true;
      await this.$nextTick();
      let upgradePath = 'manual';

      try {
        if (this.automatedCorporateUpgradesEnabled) {
          const verificationResult = await this.generateCorporateVerificationToken();

          if (verificationResult === 'verification') {
            upgradePath = 'verification';
          } else if (verificationResult === 'manual_fallback') {
            upgradePath = 'manual_fallback';
          } else {
            await this.submitLegacyEmployerMutation();
            upgradePath = 'manual_fallback';
          }
        } else {
          await this.submitLegacyEmployerMutation();
        }

        sendSegmentEvent('CorporateBenefit_RequestUpgrade', {
          employerSlugEntered: this.selectedCompanySlug,
          employerNameInput: this.employerNameInput,
          source: UPGRADE_SOURCE,
          upgradePath,
        });

        this.$emit('advanceStep', {
          upgradePath,
          ...(upgradePath === 'verification' && { workEmail: this.workEmail }),
        });
      } catch (_error) {
        this.showPopup = true;
      } finally {
        this.isLoading = false;
      }
    },
  },
  apollo: {
    corporateClients: {
      query: CORPORATE_CLIENTS_QUERY,
      result({ data }) {
        const corporateClientsForComboBox = data.corporateClients.map((corporateClient) => {
          return { display: corporateClient.name, value: corporateClient.slug };
        });
        this.corporateClients = corporateClientsForComboBox;

        const selectedClient = this.selectedCompanySlug
          ? corporateClientsForComboBox.find((client) => client.value === this.selectedCompanySlug)
          : null;

        if (selectedClient) {
          this.employerNameInput = selectedClient.display;
          this.initialValue = selectedClient.display;
          // Wait for SearchComboBox to apply initialValue before setting results,
          // otherwise a single result opens the dropdown on load.
          this.$nextTick(() => {
            this.filteredCorporateClients = [selectedClient];
          });
        } else {
          this.filteredCorporateClients = corporateClientsForComboBox;
        }
      },
    },
  },
};
</script>
